import { Select, MenuItem, Typography, Box, SelectChangeEvent, Tooltip } from "@mui/material";
import { styles } from "./stylesCustomSelect";
import CheckblackSvg from "@/assets/svg/CheckblackSvg";
import React from "react";
import ArrowDawnSvg from "../../../assets/svg/ArrowDawnSvg";
import theme from "@/theme";
import LoadingSvg from "@/assets/svg/SocialMedia/LoadingSvg";
import LockSvg from "@/assets/svg/LockSvg";
import CustomCheckbox from "../CustomCheckbox/CustomCheckbox";

type CustomSelectProps = {
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  checkbox?: boolean;
  multiple?: boolean;
  width?: string | number;
  height?: string | number;
  renderValue?: (selected: string[]) => React.ReactNode;
  disabled?: boolean;
  checkblack?: boolean;
  loading?: boolean;
  disabledOptions?: string[];
  defaultBG?: string;
};
type ArrowGrayProps = {
  hasValue: boolean;
  loading?: boolean;
};

export const ArrowGray = React.forwardRef<HTMLDivElement, ArrowGrayProps & { className?: string }>(
  ({ hasValue, loading, className, ...rest }, ref) => {
    return (
      <Box
        ref={ref}
        className={className}
        sx={{
          pr: "12px",
          display: "flex",
          alignItems: "center",
          transform: "none !important",
          "& > svg": {
            transition: "transform 0.2s",
          },
          "&.MuiSelect-iconOpen > svg": {
            transform: "rotate(180deg)",
          },
        }}
        {...rest}
      >
        {loading ? (
          <Box sx={{ mt: -0.5 }}>
            <Box
              sx={{
                width: "24px",
                height: "24px",
                display: "inline-block",
                animation: "spin 2s linear infinite",
                "@keyframes spin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
              }}
            >
              <LoadingSvg />
            </Box>
          </Box>
        ) : (
          <ArrowDawnSvg
            color={hasValue ? theme.palette.custom.blueGray900 : theme.palette.custom.blueGray500}
          />
        )}
      </Box>
    );
  }
);

const CustomSelect = ({
  options,
  selected,
  onChange,
  placeholder,
  checkbox,
  multiple,
  width,
  height = 40,
  renderValue,
  disabled,
  checkblack,
  loading,
  disabledOptions = [],
  defaultBG = "#F7F9FB",
}: CustomSelectProps) => {
  const [open, setOpen] = React.useState(false);

  const displayValue = renderValue
    ? renderValue(selected)
    : selected.length
      ? selected.join(", ")
      : placeholder;

  const handleChange = (event: SelectChangeEvent<string[] | string>) => {
    const value = event.target.value;
    if (multiple) {
      const filtered = (value as string[]).filter((v) => !disabledOptions.includes(v));
      onChange(filtered);
    } else {
      if (!value || disabledOptions.includes(value as string)) return;
      onChange([value as string]);
    }
  };

  const toggleOption = (option: string) => {
    if (disabledOptions.includes(option)) return;
    if (!multiple) {
      onChange([option]);
    } else {
      onChange(
        selected.includes(option)
          ? selected.filter((item) => item !== option)
          : [...selected, option]
      );
    }
  };

  return (
    <Select
      sx={{
        ...styles.select,
        backgroundColor: !disabled && selected.length === 0 && !open ? defaultBG : "#FFFFFF",
        color: disabled || selected.length === 0 ? "#9DA6B0" : "#2A2D31",
        width,
        height,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
      IconComponent={(props) => (
        <ArrowGray {...props} hasValue={selected.length > 0 && !disabled} loading={loading} />
      )}
      renderValue={() => displayValue}
      multiple={multiple}
      size="small"
      value={multiple ? selected : selected[0] || ""}
      onChange={handleChange}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      open={open}
      displayEmpty
      MenuProps={styles.menuProps}
      disabled={disabled}
    >
      {options.map((option) => {
        const isDisabled = disabledOptions.includes(option);

        return (
          <MenuItem
            key={option}
            value={option}
            sx={{
              ...styles.menuItem,
              cursor: isDisabled ? "not-allowed" : "pointer",
              opacity: isDisabled ? 0.5 : 1,
              width:
                typeof width === "number" ? `calc(${width}px - 20px)` : `calc(${width} - 20px)`,
            }}
            disableRipple={isDisabled}
          >
            <Box sx={styles.menuItemBox} width={width}>
              <Typography sx={styles.menuItems}>{option}</Typography>

              {isDisabled ? (
                <Tooltip
                  title={
                    <Box sx={styles.tooltipWrapper}>
                      <Typography sx={styles.tooltipText}>Available only for premium</Typography>
                    </Box>
                  }
                  placement="top"
                  componentsProps={{ tooltip: { sx: styles.tooltipProps } }}
                  PopperProps={{ modifiers: [{ name: "offset", options: { offset: [10, -10] } }] }}
                >
                  <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
                    <LockSvg width={16} height={16} />
                  </Box>
                </Tooltip>
              ) : checkbox || checkblack ? (
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleOption(option);
                  }}
                  sx={{ cursor: "pointer", display: "flex", justifyContent: "center" }}
                >
                  {checkblack ? (
                    selected.includes(option) && <CheckblackSvg />
                  ) : (
                    <CustomCheckbox
                      checked={selected.includes(option)}
                      onChange={() => toggleOption(option)}
                      size={24}
                    />
                  )}
                </Box>
              ) : null}
            </Box>
          </MenuItem>
        );
      })}
    </Select>
  );
};

export default CustomSelect;
